import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { FieldValues, UseFormReturn, FieldPath } from "react-hook-form";
import { PRESET_COLORS, DEFAULT_PRIMARY_COLOR, isValidHexColor } from "@/src/lib/theme";
import { useId } from "react";

type FormFieldColorPropsType<T extends FieldValues> = {
  form: UseFormReturn<T, unknown, undefined>;
  label: string;
  name: FieldPath<T>;
  description?: string;
};

/**
 * Pole wyboru koloru: natywny color picker (koło kolorów z przeglądarki) +
 * pole tekstowe do wpisania/wklejenia wartości HEX + paleta presetów.
 * Wartość w form state jest zawsze stringiem "#rrggbb".
 */
function FormFieldColor<T extends FieldValues>({
  form,
  label,
  name,
  description,
}: FormFieldColorPropsType<T>) {
  const pickerId = useId();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const currentValue = typeof field.value === "string" && field.value
          ? field.value
          : DEFAULT_PRIMARY_COLOR;
        const validForPicker = isValidHexColor(currentValue) ? currentValue : DEFAULT_PRIMARY_COLOR;

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="flex flex-col gap-3">
                {/* Picker + tekst */}
                <div className="flex items-center gap-2">
                  <label
                    htmlFor={pickerId}
                    className="relative w-10 h-10 rounded-md border border-gray-300 overflow-hidden cursor-pointer shrink-0"
                    style={{ backgroundColor: validForPicker }}
                    title="Otwórz koło kolorów">
                    <input
                      id={pickerId}
                      type="color"
                      value={validForPicker}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </label>
                  <input
                    type="text"
                    value={currentValue}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="#1d4ed8"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400"
                  />
                </div>

                {/* Presety — szybki wybór typowych kolorów */}
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((preset) => {
                    const isSelected = currentValue.toLowerCase() === preset.hex.toLowerCase();
                    return (
                      <button
                        key={preset.hex}
                        type="button"
                        onClick={() => field.onChange(preset.hex)}
                        title={`${preset.label} (${preset.hex})`}
                        className={`w-7 h-7 rounded-md border transition-transform ${
                          isSelected
                            ? "border-gray-900 ring-2 ring-gray-900 ring-offset-1 scale-110"
                            : "border-gray-300 hover:scale-110"
                        }`}
                        style={{ backgroundColor: preset.hex }}
                      />
                    );
                  })}
                </div>
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default FormFieldColor;
